import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Row, Col, Drawer, Checkbox, Select, Input, Collapse, notification, Tag, Dropdown } from 'antd'
import { CloseOutlined, CheckOutlined, SearchOutlined } from '@ant-design/icons'
import { saveColumnsAndExtraColumns, setDrawerVisibility } from '../../../Slices/Kiosk/TableOptionsSlice'
import { populateTableWithArticles } from '../../../Services/Kiosk/ArticleServices'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { renderTranslation } from '../../../Helpers/GenericHelpers'
import { setCapitalLetter, unsensitiveCaseSearchMatch } from '../../../Helpers/StringHelpers'
import {
  LanguageList,
  PredefinedFilters,
  ExtraFilters,
  PriceFilters,
  OptionalsFilters,
  WebAndMarketingFilters,
  UnretrievedTranslation,
  FilterLangClass,
  ProfilesKeys,
} from '../../../Constants/Configuration'
import { ArticleColumn } from '../../../Constants/TableConfiguration'
import {
  setShowExtraFilterElement,
  setShowOptionalFilterElement,
  setShowPredefinedFilterElement,
  setShowWebAndMarketingFilterElement,
  setShwoPriceFilterElement,
} from '../../../Slices/ConfigurationSlice'
import { getActiveFilterValue } from '../../../Helpers/FilterHelper'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { checkPricelistVisibilityOnFilters, workspacesToExclude } from 'Helpers/ArticlesHelper'
import { WorkSpace } from 'Constants/WorkSpace'

const { Option } = Select
const { Panel } = Collapse

function ArticleTableFilter() {
  const dispatch = useAppDispatch()
  const tableColumns = useAppSelector(state => state.present.tableOptions.tableColumns)
  const showDrawer = useAppSelector(state => state.present.tableOptions.showDrawer)
  const language = useAppSelector(state => state.present.language.value)
  const currentLang = useAppSelector(state => state.present.language.current)
  const priceList1 = useAppSelector(state => state.present.tableOptions.priceList1)
  const priceList2 = useAppSelector(state => state.present.tableOptions.priceList2)
  const emptyTableColumns = useAppSelector(state => state.present.tableOptions.emptyTableColumns)
  const extraDescriptionLang = useAppSelector(state => state.present.tableOptions.extraDescriptionLang)
  const labelClickOnFilters = useAppSelector(state => state.present.configuration.LabelClickOnFilters)
  const emptyExtraDescriptions = useAppSelector(state => state.present.tableOptions.emptyExtraDescriptions)
  const pricelists = useAppSelector(state => state.present.tableOptions.pricelists)
  const allMarketplace = useAppSelector(state => state.present.configuration.allMarketplace)
  const profileValue = useAppSelector(state => state.present.configuration.profileValue)
  const activeTheme = useAppSelector(state => state.present.configuration.activeTheme)
  const isPurchasePriceVisibile = useAppSelector(state => state.present.configuration.isPurchasePriceVisibile)
  const currentConfig = useAppSelector(state => state.present.configuration.currentConfiguration)

  const predefinedFiltersVisible = useAppSelector(
    state => state.present.configuration.workspacePredefinedFiltersVisible,
  )
  const extraFiltersVisible = useAppSelector(state => state.present.configuration.workspaceExtraFiltersVisible)
  const priceFiltersVisible = useAppSelector(state => state.present.configuration.workspacePriceFiltersVisible)
  const optionalFiltersVisible = useAppSelector(state => state.present.configuration.workspaceOptionalFiltersVisible)
  const webAndMarketingFiltersVisible = useAppSelector(
    state => state.present.configuration.workspaceWebAndMarketingFiltersVisible,
  )

  const [selectedFilters, setSelectedFilters] = useState(JSON.parse(JSON.stringify(tableColumns)))
  const [selectedPricelists, setSelectedPricelists] = useState(JSON.parse(JSON.stringify(pricelists)))
  const [emptyFilters, setEmptyFilters] = useState(JSON.parse(JSON.stringify(emptyTableColumns)))
  const [extraDescriptionFilters, setExtraDescriptionFilters] = useState(
    JSON.parse(JSON.stringify(extraDescriptionLang)),
  )
  const [searchValue, setSearchValue] = useState('')
  const [activeKey, setActiveKey] = useState([])

  const changeActiveKey = useCallback((key: string[]) => {
    setActiveKey(key)
  }, [])

  const filterBySearchedValue = useCallback(
    filters => {
      if (!searchValue || searchValue.length < 2) return filters

      return filters.filter(({ value }) => {
        const filterLabel = renderTranslation(language, TranslationKeys[FilterLangClass + setCapitalLetter(value)])
        if (filterLabel && filterLabel !== UnretrievedTranslation) {
          return unsensitiveCaseSearchMatch(filterLabel, searchValue)
        } else {
          return unsensitiveCaseSearchMatch(value, searchValue)
        }
      })
    },
    [searchValue, language],
  )

  const visiblePredefinedFilters = useMemo(() => {
    return filterBySearchedValue(selectedFilters.predefinedFilters.filter(filter => filter.visible))
  }, [selectedFilters.predefinedFilters, filterBySearchedValue])
  const visibleExtraFilters = useMemo(() => {
    return filterBySearchedValue(selectedFilters.extraFilters.filter(filter => filter.visible))
  }, [selectedFilters.extraFilters, filterBySearchedValue])
  const visiblePriceFilters = useMemo(() => {
    return filterBySearchedValue(selectedFilters.priceFilters.filter(filter => filter.visible))
  }, [selectedFilters.priceFilters, filterBySearchedValue])
  const visibleOptionalsFilters = useMemo(() => {
    return filterBySearchedValue(selectedFilters.optionalsFilters.filter(filter => filter.visible))
  }, [selectedFilters.optionalsFilters, filterBySearchedValue])
  const visibleWebAndMarketingFilters = useMemo(() => {
    return filterBySearchedValue(selectedFilters.webAndMarketingFilters.filter(filter => filter.visible))
  }, [selectedFilters.webAndMarketingFilters, filterBySearchedValue])
  const NotvisibleWebAndMarketingFilters = useMemo(() => {
    return filterBySearchedValue(selectedFilters.webAndMarketingFilters.filter(filter => !filter.visible))
  }, [selectedFilters.webAndMarketingFilters, filterBySearchedValue])

  const showPriceLists = useMemo(() => {
    return (
      !searchValue ||
      searchValue.length < 2 ||
      renderTranslation(language, TranslationKeys.pricelist1).toLowerCase().indexOf(searchValue.toLowerCase()) !== -1 ||
      renderTranslation(language, TranslationKeys.pricelist2).toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
    )
  }, [language, searchValue])

  const retrieveActiveKeysPanel = useMemo(() => {
    const collapsePanelKeys = []
    if (visiblePredefinedFilters.length !== 0) {
      collapsePanelKeys.push(1)
    }
    if (visibleExtraFilters.length !== 0) {
      collapsePanelKeys.push(2)
    }
    if (
      visiblePriceFilters.length !== 0 ||
      ((selectedPricelists.priceList1Visible || selectedPricelists.priceList2Visible) && showPriceLists)
    ) {
      collapsePanelKeys.push(3)
    }
    if (visibleOptionalsFilters.length !== 0) {
      collapsePanelKeys.push(4)
    }
    if (visibleWebAndMarketingFilters.length !== 0) {
      collapsePanelKeys.push(5)
    }

    return collapsePanelKeys
  }, [
    visiblePredefinedFilters,
    visibleExtraFilters,
    visiblePriceFilters,
    visibleOptionalsFilters,
    visibleWebAndMarketingFilters,
    selectedPricelists.priceList1Visible,
    selectedPricelists.priceList2Visible,
    showPriceLists,
  ])

  useEffect(() => {
    setSelectedFilters(JSON.parse(JSON.stringify(tableColumns)))
    setSelectedPricelists(JSON.parse(JSON.stringify(pricelists)))
    setEmptyFilters(JSON.parse(JSON.stringify(emptyTableColumns)))
    setExtraDescriptionFilters(JSON.parse(JSON.stringify(extraDescriptionLang)))
  }, [tableColumns, emptyTableColumns, extraDescriptionLang, pricelists])

  useEffect(() => {
    if (activeKey.length === 0 && !(retrieveActiveKeysPanel.length === 1 && retrieveActiveKeysPanel[0] === 5)) {
      setActiveKey(retrieveActiveKeysPanel)
    }
  }, [retrieveActiveKeysPanel, activeKey.length])

  const currentKey = useMemo(() => {
    return !searchValue || searchValue.length < 2 ? activeKey : retrieveActiveKeysPanel
  }, [retrieveActiveKeysPanel, searchValue, activeKey])

  const closeAndSaveFilter = useCallback(
    e => {
      const objectToSave = {
        tableColumns: JSON.parse(JSON.stringify(selectedFilters)),
        emptyTableColumns: JSON.parse(JSON.stringify(emptyFilters)),
        extraDescriptionLang: JSON.parse(JSON.stringify(extraDescriptionFilters)),
        allMkt: JSON.parse(JSON.stringify(allMarketplace)),
        pricelists: JSON.parse(JSON.stringify(selectedPricelists)),
      }

      const emptyChanges = []
      const keys = Object.keys(emptyTableColumns)
      keys.forEach(element => {
        const pastEmpty = emptyTableColumns[element]
        const currentEmpty = emptyFilters[element]

        pastEmpty.forEach(pastElement => {
          currentEmpty.forEach(currentElement => {
            if (currentElement.value === pastElement.value && currentElement.active !== pastElement.active) {
              emptyChanges.push(currentElement)
            }
          })
        })
      })

      dispatch(saveColumnsAndExtraColumns(objectToSave))

      //always search, if column is removed filter follow that change (check saveColumnsAndExtraColumns logic)
      dispatch(populateTableWithArticles())
    },
    [
      selectedFilters,
      emptyFilters,
      extraDescriptionFilters,
      allMarketplace,
      dispatch,
      emptyTableColumns,
      selectedPricelists,
    ],
  )

  const onChangeCheckbox = useCallback(
    (checkedValues, filterGroup) => {
      const currentFilters = JSON.parse(JSON.stringify(selectedFilters))
      const currentEmptyFilters = JSON.parse(JSON.stringify(emptyFilters))
      for (const group in currentFilters) {
        if (group === filterGroup) {
          const filteredGroup = filterBySearchedValue(currentFilters[group])
          for (const filter in filteredGroup) {
            if (checkedValues.indexOf(filteredGroup[filter].value) !== -1) {
              filteredGroup[filter].active = true

              //language flag
              if (
                filteredGroup[filter].value === ArticleColumn.ShortDescription ||
                filteredGroup[filter].value === ArticleColumn.SpecialDescription ||
                filteredGroup[filter].value === ArticleColumn.Description ||
                filteredGroup[filter].value === ArticleColumn.SizeAndFit
              ) {
                let hasLanguage = false
                Object.keys(extraDescriptionFilters).forEach(lang => {
                  hasLanguage = hasLanguage || extraDescriptionFilters[lang].indexOf(filteredGroup[filter].value) !== -1
                })

                if (!hasLanguage) {
                  extraDescriptionFilters[currentLang].push(filteredGroup[filter].value)
                }
              }
            } else {
              filteredGroup[filter].active = false

              if (
                filteredGroup[filter].value === ArticleColumn.ShortDescription ||
                filteredGroup[filter].value === ArticleColumn.SpecialDescription ||
                filteredGroup[filter].value === ArticleColumn.Description ||
                filteredGroup[filter].value === ArticleColumn.SizeAndFit
              ) {
                Object.keys(extraDescriptionFilters).forEach(lang => {
                  extraDescriptionFilters[lang] = extraDescriptionFilters[lang].filter(
                    filterName => filterName !== filteredGroup[filter].value,
                  )
                })
              }

              if (currentEmptyFilters[group]) {
                currentEmptyFilters[group].forEach(element => {
                  if (element.value === filteredGroup[filter].value) {
                    element.active = false
                  }
                })
              }
            }
          }
        }
      }

      if (
        currentFilters.priceFilters.reduce((curr, prev) => {
          return curr + (prev.active ? 1 : 0)
        }, 0) > 2
      ) {
        return notification.open({
          description: renderTranslation(language, TranslationKeys.maxTwoPrices),
          placement: 'topLeft',
          message: '',
        })
      }

      setSelectedFilters(currentFilters)
      setEmptyFilters(currentEmptyFilters)
      setExtraDescriptionFilters(extraDescriptionFilters)
    },
    [selectedFilters, emptyFilters, extraDescriptionFilters, currentLang, filterBySearchedValue, language],
  )

  const handlePriceList1 = useCallback(
    value => {
      const currentPricelists = JSON.parse(JSON.stringify(selectedPricelists))
      if (currentPricelists.priceList2 === value) {
        return notification.open({
          description: renderTranslation(language, TranslationKeys.duplicatedPricelist),
          placement: 'topLeft',
          message: '',
        })
      }
      currentPricelists.priceList1 = value
      setSelectedPricelists(currentPricelists)
    },
    [selectedPricelists, language],
  )
  const handlePriceList2 = useCallback(
    value => {
      const currentPricelists = JSON.parse(JSON.stringify(selectedPricelists))
      if (currentPricelists.priceList1 === value) {
        return notification.open({
          description: renderTranslation(language, TranslationKeys.duplicatedPricelist),
          placement: 'topLeft',
          message: '',
        })
      }
      currentPricelists.priceList2 = value
      setSelectedPricelists(currentPricelists)
    },
    [selectedPricelists, language],
  )

  const onChangePriceList1 = useCallback(
    e => {
      const currentPricelists = JSON.parse(JSON.stringify(selectedPricelists))

      if (e.target.checked && !currentPricelists.priceList1) {
        return notification.open({
          description: renderTranslation(language, TranslationKeys.emptyPricelist),
          placement: 'topLeft',
          message: '',
        })
      }

      currentPricelists.priceList1Enabled = e.target.checked
      setSelectedPricelists(currentPricelists)
    },
    [selectedPricelists, language],
  )

  const onChangePriceList2 = useCallback(
    e => {
      const currentPricelists = JSON.parse(JSON.stringify(selectedPricelists))
      if (e.target.checked && !currentPricelists.priceList2) {
        return notification.open({
          description: renderTranslation(language, TranslationKeys.emptyPricelist),
          placement: 'topLeft',
          message: '',
        })
      }
      currentPricelists.priceList2Enabled = e.target.checked
      setSelectedPricelists(currentPricelists)
    },
    [selectedPricelists, language],
  )

  const closeFilterDrawer = useCallback(() => {
    dispatch(setDrawerVisibility(false))
    setSelectedFilters(JSON.parse(JSON.stringify(tableColumns)))
    setEmptyFilters(JSON.parse(JSON.stringify(emptyTableColumns)))
    setExtraDescriptionFilters(JSON.parse(JSON.stringify(extraDescriptionLang)))
  }, [dispatch, tableColumns, emptyTableColumns, extraDescriptionLang])

  const emptyFilter = useCallback(
    e => {
      if (labelClickOnFilters) {
        e.stopPropagation()
        e.preventDefault()
        const value = e.target.parentElement.parentElement.querySelector('input').value
        if (value) {
          const currentEmptyFilters = JSON.parse(JSON.stringify(emptyFilters))
          const currentlySelectedFilters = JSON.parse(JSON.stringify(selectedFilters))
          for (const group in currentEmptyFilters) {
            for (const emptyFilter in currentEmptyFilters[group]) {
              if (currentEmptyFilters[group][emptyFilter].value === value) {
                currentEmptyFilters[group][emptyFilter].active = !currentEmptyFilters[group][emptyFilter].active
                currentlySelectedFilters[group].forEach(element => {
                  if (element.value === value) {
                    element.active = currentEmptyFilters[group][emptyFilter].active
                  }
                })
              }
            }
          }
          setEmptyFilters(currentEmptyFilters)
          setSelectedFilters(currentlySelectedFilters)
        }
      }
    },
    [emptyFilters, selectedFilters, labelClickOnFilters],
  )

  //language flag
  const saveLanguageForDescription = useCallback(
    (lang, descriptionName) => {
      const currentLangFilters = JSON.parse(JSON.stringify(extraDescriptionFilters))
      const elementIndex = currentLangFilters[lang].indexOf(descriptionName)
      if (elementIndex === -1) {
        currentLangFilters[lang].push(descriptionName)
      } else {
        const checkExtraDescription = []
        Object.keys(extraDescriptionFilters).forEach(lang => {
          if (extraDescriptionFilters[lang].indexOf(descriptionName) !== -1) {
            checkExtraDescription.push(lang)
          }
        })

        if (checkExtraDescription.length > 1) {
          currentLangFilters[lang].splice(elementIndex, 1)
        }
      }
      setExtraDescriptionFilters(currentLangFilters)
    },
    [extraDescriptionFilters],
  )

  const hideSectionMenu = useCallback(
    setVisibility => {
      return {
        items: [
          {
            key: 'disable',
            label: 'Disabilita',
            onClick: () => {
              dispatch(setVisibility(false))
            },
          },
        ],
      }
    },
    [dispatch],
  )

  return (
    <Drawer
      placement='left'
      onClose={closeFilterDrawer}
      open={showDrawer}
      getContainer={document.getElementById('#page-content-diwa')}
      closable={false}
      rootClassName={'filter-drawer custom-scroll-body position-absolute' + activeTheme}
    >
      <div className='background-primary sticky-head' id='filter-drawer-head-container'>
        <div className='filter-drawer-head-row'>
          {renderTranslation(language, TranslationKeys.filtersTitle)}
          <span className='float-right'>
            <CheckOutlined className='filter-drawer-check-icon' onClick={closeAndSaveFilter} />
            <CloseOutlined className='filter-drawer-close-icon' onClick={closeFilterDrawer} />
          </span>
        </div>
      </div>
      <Collapse activeKey={currentKey} onChange={changeActiveKey}>
        {predefinedFiltersVisible ? (
          <Panel
            className='filter-drawer-panel'
            header={
              <>
                <Dropdown
                  menu={hideSectionMenu(setShowPredefinedFilterElement)}
                  trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
                >
                  <span className='panel-title'>{renderTranslation(language, TranslationKeys.filtersPrincipal)}</span>
                </Dropdown>
                <span className='drawer-input-container'>
                  <Input
                    prefix={<SearchOutlined />}
                    rootClassName='drawer-search'
                    value={searchValue}
                    onChange={e => {
                      setSearchValue(e.target.value)
                    }}
                    onClick={e => {
                      e.stopPropagation()
                    }}
                    allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
                  />
                </span>
              </>
            }
            key={1}
            collapsible={visiblePredefinedFilters.length !== 0 ? 'header' : 'disabled'}
          >
            <Checkbox.Group
              rootClassName='w-100'
              onChange={checkedValues => {
                onChangeCheckbox(checkedValues, PredefinedFilters)
              }}
              value={getActiveFilterValue(filterBySearchedValue(selectedFilters.predefinedFilters))}
            >
              <Row>
                {visiblePredefinedFilters.map(({ value }) => {
                  const isEmpty =
                    emptyFilters.predefinedFilters.filter(element => element.value === value && element.active)
                      .length !== 0
                  return (
                    <Col key={value} span={12}>
                      <Checkbox
                        rootClassName={
                          !isEmpty
                            ? 'filter-drawer-checkbox general-checkbox'
                            : 'filter-drawer-checkbox general-checkbox empty'
                        }
                        value={value}
                      >
                        <span
                          onClick={emptyFilter}
                          className={!isEmpty ? 'filter-drawer-label' : 'filter-drawer-label empty'}
                        >
                          {!isEmpty
                            ? renderTranslation(language, TranslationKeys[FilterLangClass + setCapitalLetter(value)])
                            : renderTranslation(language, TranslationKeys.emptyFilterIncipit) +
                              ' ' +
                              renderTranslation(language, TranslationKeys[FilterLangClass + setCapitalLetter(value)])}
                        </span>
                      </Checkbox>
                    </Col>
                  )
                })}
              </Row>
            </Checkbox.Group>
          </Panel>
        ) : null}
        {extraFiltersVisible ? (
          <Panel
            className='filter-drawer-panel'
            header={
              <>
                <Dropdown
                  menu={hideSectionMenu(setShowExtraFilterElement)}
                  trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
                >
                  <span className='panel-title'>{renderTranslation(language, TranslationKeys.filtersExtra)}</span>
                </Dropdown>
              </>
            }
            key={2}
            collapsible={visibleExtraFilters.length !== 0 ? 'header' : 'disabled'}
          >
            <Checkbox.Group
              rootClassName='w-100'
              onChange={checkedValues => {
                onChangeCheckbox(checkedValues, ExtraFilters)
              }}
              value={getActiveFilterValue(filterBySearchedValue(selectedFilters.extraFilters))}
            >
              <Row>
                {visibleExtraFilters.map(({ value, active }) => {
                  const isEmpty =
                    emptyFilters.extraFilters.filter(element => element.value === value && element.active).length !== 0
                  return (
                    <div key={value} className='w-100'>
                      <Col
                        key={value}
                        span={24}
                        onClick={e => {
                          e.preventDefault()
                        }}
                      >
                        <Checkbox
                          rootClassName={
                            !isEmpty
                              ? 'filter-drawer-checkbox general-checkbox'
                              : 'filter-drawer-checkbox general-checkbox empty'
                          }
                          value={value}
                        >
                          <span
                            onClick={emptyFilter}
                            className={!isEmpty ? 'filter-drawer-label' : 'filter-drawer-label empty'}
                          >
                            {!isEmpty
                              ? renderTranslation(language, TranslationKeys[FilterLangClass + setCapitalLetter(value)])
                              : renderTranslation(language, TranslationKeys.emptyFilterIncipit) +
                                ' ' +
                                renderTranslation(language, TranslationKeys[FilterLangClass + setCapitalLetter(value)])}
                          </span>
                        </Checkbox>
                      </Col>
                      {value === ArticleColumn.ShortDescription ||
                      value === ArticleColumn.SpecialDescription ||
                      value === ArticleColumn.Description ||
                      value === ArticleColumn.SizeAndFit ? (
                        <Col key={'flag-row-' + value} span={24}>
                          {LanguageList.map(language => {
                            const isEmptyExtra = emptyExtraDescriptions.filter(
                              element => element.field === value && element.languageCode === language,
                            )
                            return (
                              <Tag
                                key={'flag-tag-' + value + '-' + language}
                                rootClassName={
                                  extraDescriptionFilters[language].indexOf(value) !== -1
                                    ? 'lang-tag active cursor-pointer' + (isEmptyExtra.length !== 0 ? ' empty' : '')
                                    : 'lang-tag cursor-pointer'
                                }
                                onClick={e => {
                                  if (!active) {
                                    return
                                  }
                                  saveLanguageForDescription(language, value)
                                }}
                              >
                                {language}
                              </Tag>
                            )
                          })}
                        </Col>
                      ) : null}
                    </div>
                  )
                })}
              </Row>
            </Checkbox.Group>
          </Panel>
        ) : null}
        {priceFiltersVisible ? (
          <Panel
            className='filter-drawer-panel'
            header={
              <>
                <Dropdown
                  menu={hideSectionMenu(setShwoPriceFilterElement)}
                  trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
                >
                  <span className='panel-title'>{renderTranslation(language, TranslationKeys.filtersPrices)}</span>
                </Dropdown>
              </>
            }
            key={3}
            collapsible={
              (visiblePriceFilters.length !== 0 && showPriceLists) ||
              (visiblePriceFilters.length === 0 && showPriceLists)
                ? 'header'
                : 'disabled'
            }
          >
            <Row>
              <Col span={12}>
                <Checkbox.Group
                  rootClassName='w-100'
                  onChange={checkedValues => {
                    onChangeCheckbox(checkedValues, PriceFilters)
                  }}
                  value={getActiveFilterValue(filterBySearchedValue(selectedFilters.priceFilters))}
                >
                  <Row>
                    {visiblePriceFilters.map(({ value }) => {
                      return (isPurchasePriceVisibile && value === ArticleColumn.GrossWholesalePrice) ||
                        (isPurchasePriceVisibile && value === ArticleColumn.WholesalePrice) ||
                        (value !== ArticleColumn.GrossWholesalePrice && value !== ArticleColumn.WholesalePrice) ? (
                        <Col key={value} span={24}>
                          <Checkbox rootClassName='filter-drawer-checkbox general-checkbox' value={value}>
                            <span className='filter-drawer-label'>
                              {renderTranslation(language, TranslationKeys[FilterLangClass + setCapitalLetter(value)])}
                            </span>
                          </Checkbox>
                        </Col>
                      ) : null
                    })}
                  </Row>
                </Checkbox.Group>
              </Col>
              {(selectedPricelists.priceList1Visible || selectedPricelists.priceList2Visible) && showPriceLists && (
                <Col span={12}>
                  {selectedPricelists.priceList1Visible && (
                    <>
                      <Checkbox
                        rootClassName='filter-drawer-checkbox general-checkbox w-100'
                        checked={selectedPricelists.priceList1Enabled}
                        onChange={onChangePriceList1}
                      >
                        <span className='filter-drawer-label'>
                          {renderTranslation(language, TranslationKeys.pricelist1)}
                        </span>
                      </Checkbox>
                      <Select
                        defaultValue='site'
                        rootClassName='w-120-p'
                        onChange={handlePriceList1}
                        value={selectedPricelists.priceList1}
                      >
                        {priceList1.map(x =>
                          workspacesToExclude(currentConfig) ? (
                            x.name === 'M/V' ? (
                              <Option key={'site-' + x.name} value={x.name} disabled={true}>
                                {x.name}
                              </Option>
                            ) : null
                          ) : checkPricelistVisibilityOnFilters(x.typeOfChannel, currentConfig, x.name) ? (
                            <Option key={'site-' + x.name} value={x.name}>
                              {x.name}
                            </Option>
                          ) : null,
                        )}
                      </Select>
                    </>
                  )}
                  {selectedPricelists.priceList2Visible && (
                    <>
                      <Checkbox
                        rootClassName='filter-drawer-checkbox general-checkbox w-100'
                        checked={selectedPricelists.priceList2Enabled}
                        onChange={onChangePriceList2}
                        disabled={currentConfig === WorkSpace.STORE}
                      >
                        <span className='filter-drawer-label'>
                          {renderTranslation(language, TranslationKeys.pricelist2)}
                        </span>
                      </Checkbox>
                      <Select
                        defaultValue='variant'
                        rootClassName='w-120-p'
                        onChange={handlePriceList2}
                        value={selectedPricelists.priceList2}
                        disabled={currentConfig === WorkSpace.STORE}
                      >
                        {priceList2.map(x => {
                          return checkPricelistVisibilityOnFilters(x.typeOfChannel, currentConfig, x.name) ? (
                            <Option key={'variant-' + x.name} value={x.name}>
                              {x.name}
                            </Option>
                          ) : null
                        })}
                      </Select>
                    </>
                  )}
                </Col>
              )}
            </Row>
          </Panel>
        ) : null}
        {optionalFiltersVisible ? (
          <Panel
            className='filter-drawer-panel'
            header={
              <>
                <Dropdown
                  menu={hideSectionMenu(setShowOptionalFilterElement)}
                  trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
                >
                  <span className='panel-title'>{renderTranslation(language, TranslationKeys.filtersOptional)}</span>
                </Dropdown>
              </>
            }
            key={4}
            collapsible={visibleOptionalsFilters.length !== 0 ? 'header' : 'disabled'}
          >
            <Checkbox.Group
              rootClassName='w-100'
              onChange={checkedValues => {
                onChangeCheckbox(checkedValues, OptionalsFilters)
              }}
              value={getActiveFilterValue(filterBySearchedValue(selectedFilters.optionalsFilters))}
            >
              <Row>
                {visibleOptionalsFilters.map(({ value }) => {
                  return (
                    <Col key={value} span={12}>
                      <Checkbox rootClassName='filter-drawer-checkbox general-checkbox' value={value}>
                        <span className='filter-drawer-label'>
                          {renderTranslation(language, TranslationKeys[FilterLangClass + setCapitalLetter(value)])}
                        </span>
                      </Checkbox>
                    </Col>
                  )
                })}
              </Row>
            </Checkbox.Group>
          </Panel>
        ) : null}
        {webAndMarketingFiltersVisible ? (
          <Panel
            className='filter-drawer-panel'
            header={
              <>
                <Dropdown
                  menu={hideSectionMenu(setShowWebAndMarketingFilterElement)}
                  trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
                >
                  <span className='panel-title'>
                    {renderTranslation(language, TranslationKeys.filterWebAndMarketplace)}
                  </span>
                </Dropdown>
              </>
            }
            key={5}
            collapsible={visibleWebAndMarketingFilters.length !== 0 ? 'header' : 'disabled'}
          >
            <Checkbox.Group
              rootClassName='w-100'
              onChange={checkedValues => {
                onChangeCheckbox(checkedValues, WebAndMarketingFilters)
              }}
              value={getActiveFilterValue(filterBySearchedValue(selectedFilters.webAndMarketingFilters))}
            >
              <Row>
                {selectedFilters.webAndMarketingFilters && selectedFilters.webAndMarketingFilters.length !== 0
                  ? visibleWebAndMarketingFilters.map(({ value }, index) => {
                      return (
                        <Col key={value + index} span={12}>
                          <Checkbox rootClassName='filter-drawer-checkbox general-checkbox' value={value}>
                            <span onClick={emptyFilter} className={'filter-drawer-label'}>
                              {value}
                            </span>
                          </Checkbox>
                        </Col>
                      )
                    })
                  : null}
              </Row>
            </Checkbox.Group>
            <Row className='marketplace-notvisible'>
              {selectedFilters.webAndMarketingFilters && selectedFilters.webAndMarketingFilters.length !== 0
                ? NotvisibleWebAndMarketingFilters.filter(
                    ({ value }, index) => !value.toLowerCase().includes('sito'),
                  ).map(({ value }, index) => {
                    return (
                      <span className='pl-2' onClick={emptyFilter} key={value}>
                        {value},
                      </span>
                    )
                  })
                : null}
            </Row>
          </Panel>
        ) : null}
      </Collapse>
    </Drawer>
  )
}

export default ArticleTableFilter
